import { useMemo } from "react";
import {
	type LimitOverrides,
	isOutOfLimit,
	limitRangeLabel,
} from "../../straightener-common/paramLimits";
import type { StraightenerParameter } from "../../straightener-common/parameterCatalog";
import {
	type Cell,
	PRODUCT_DIM_CODES,
	cellKey,
	numOrNull,
} from "../components/recipeFormUtils";

interface RecipeValidationInput {
	cells: Record<string, Cell>;
	fieldSections: StraightenerParameter[][];
	perRollParams: StraightenerParameter[];
	rollCount: number;
	requiredCodes: Set<string>;
	limitOverrides: LimitOverrides;
	name: string;
	existingNames?: string[];
}

// The recipe editor's three validation engines, derived from the form cells:
// - limits: values outside the configured Min/Max limits (block saving)
// - required: catalog-required fields left empty (recipe saves as draft)
// - bands: per-roll Min < Max and Target inside the band (block saving)
export const useRecipeValidation = ({
	cells,
	fieldSections,
	perRollParams,
	rollCount,
	requiredCodes,
	limitOverrides,
	name,
	existingNames,
}: RecipeValidationInput) => {
	// Recipe name is required and unique per product (the SP enforces it too).
	const trimmedName = name.trim();
	const nameError = useMemo(() => {
		if (!trimmedName) return "Recipe name is required.";
		if ((existingNames ?? []).includes(trimmedName.toLowerCase()))
			return "A recipe with this name already exists for this product.";
		return null;
	}, [trimmedName, existingNames]);

	// Out-of-limit validation over the visible (editable, numeric) fields.
	const limitErrors = useMemo(() => {
		const bad: { code: string; label: string }[] = [];
		for (const list of fieldSections) {
			for (const p of list) {
				if (p.valueType !== "numeric" || p.readOnly) continue;
				if (PRODUCT_DIM_CODES.has(p.code)) continue; // fixed by product
				if (
					isOutOfLimit(
						p.code,
						numOrNull(cells[cellKey(p.code, 0)]?.value ?? ""),
						limitOverrides,
					)
				)
					bad.push({ code: p.code, label: p.displayName });
			}
		}
		// Per-roll targets share one global limit per parameter; the target AND
		// the Min/Max tolerance band all validate against the parameter's limits.
		for (const p of perRollParams) {
			if (p.valueType !== "numeric" || p.readOnly) continue;
			for (let roll = 1; roll <= rollCount; roll++) {
				const c = cells[cellKey(p.code, roll)];
				const bands: [string, string][] = [
					["", c?.value ?? ""],
					[" Min", c?.min ?? ""],
					[" Max", c?.max ?? ""],
				];
				for (const [suffix, raw] of bands) {
					if (isOutOfLimit(p.code, numOrNull(raw), limitOverrides))
						bad.push({
							code: p.code,
							label: `${p.displayName}${suffix} (Roll #${roll})`,
						});
				}
			}
		}
		return bad;
	}, [cells, fieldSections, perRollParams, limitOverrides, rollCount]);
	const limitErrorCount = limitErrors.length;
	const hasLimitErrors = limitErrorCount > 0;

	// Required-field validation (catalog-driven): an empty required field shows
	// the same inline error UX as the recipe name, so the backend guard never
	// fires from the UI.
	const missingRequired = useMemo(() => {
		const bad: { code: string; label: string }[] = [];
		for (const list of fieldSections) {
			for (const p of list) {
				if (p.readOnly || !requiredCodes.has(p.code)) continue;
				const c = cells[cellKey(p.code, 0)];
				const empty =
					p.valueType === "select"
						? !(c?.text ?? "")
						: numOrNull(c?.value ?? "") == null;
				if (empty) bad.push({ code: p.code, label: p.displayName });
			}
		}
		for (const p of perRollParams) {
			if (p.valueType !== "numeric" || p.readOnly) continue;
			if (!requiredCodes.has(p.code)) continue;
			for (let roll = 1; roll <= rollCount; roll++) {
				if (numOrNull(cells[cellKey(p.code, roll)]?.value ?? "") == null)
					bad.push({ code: p.code, label: `${p.displayName} (Roll #${roll})` });
			}
		}
		return bad;
	}, [cells, fieldSections, perRollParams, requiredCodes, rollCount]);
	const missingCount = missingRequired.length;

	// Roll Min/Max bands must satisfy min < max (strictly) when both are set.
	const bandInvalid = (code: string, roll: number): boolean => {
		const c = cells[cellKey(code, roll)];
		const mn = numOrNull(c?.min ?? "");
		const mx = numOrNull(c?.max ?? "");
		return mn != null && mx != null && mn >= mx;
	};
	// Target must sit inside its own Min/Max band.
	const targetOutsideBand = (code: string, roll: number): boolean => {
		const c = cells[cellKey(code, roll)];
		const t = numOrNull(c?.value ?? "");
		if (t == null) return false;
		const mn = numOrNull(c?.min ?? "");
		const mx = numOrNull(c?.max ?? "");
		return (mn != null && t < mn) || (mx != null && t > mx);
	};
	const bandErrorCount = useMemo(() => {
		let n = 0;
		for (const p of perRollParams) {
			if (!p.hasMinMax) continue;
			for (let roll = 1; roll <= rollCount; roll++)
				if (bandInvalid(p.code, roll) || targetOutsideBand(p.code, roll)) n++;
		}
		return n;
		// biome-ignore lint/correctness/useExhaustiveDependencies: helpers read cells
	}, [cells, perRollParams, rollCount]);

	// Out-of-range values and invalid bands block saving; missing required
	// fields save the recipe as an inactive draft instead.
	const saveBlocked = hasLimitErrors || bandErrorCount > 0;
	// One bad field → name it and show its range; several → just the count.
	const limitErrorMessage = useMemo(() => {
		if (limitErrorCount === 0) return "";
		if (limitErrorCount === 1) {
			const p = limitErrors[0];
			return `${p.label} out of range (${limitRangeLabel(
				p.code,
				limitOverrides,
			)}) — fix to save.`;
		}
		return `${limitErrorCount} values out of range — fix to save.`;
	}, [limitErrors, limitErrorCount, limitOverrides]);
	const blockErrorMessage =
		limitErrorCount > 0
			? limitErrorMessage
			: `${bandErrorCount} roll band error(s) (Min < Max, Target inside the band) — fix to save.`;
	const draftMessage =
		missingCount === 1
			? `${missingRequired[0].label} is empty — the recipe will be saved as an inactive draft.`
			: `${missingCount} required fields are empty — the recipe will be saved as an inactive draft.`;

	return {
		trimmedName,
		nameError,
		limitErrors,
		limitErrorCount,
		hasLimitErrors,
		missingRequired,
		missingCount,
		bandInvalid,
		targetOutsideBand,
		bandErrorCount,
		saveBlocked,
		blockErrorMessage,
		draftMessage,
	};
};
